import type { IntlShape } from 'react-intl'
import { formatDayLabel } from 'tripPlanner/utils/chatDates'
import type { TripStop } from 'tripPlanner/api/types'
import { isHotelSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import type { TripListRow } from './tripListTypes'

export interface TripListMoveDayOption {
  id: string
  title: string
  subtitle: string
  selected: boolean
  target:
    | { targetType: 'day'; dayId: number }
    | { targetType: 'unplanned' }
}

function getSavedStayDuration(row: TripListRow): number | undefined {
  if (row.kind !== 'item') return undefined
  const savedItemData = row.item.savedItemData
  if (!isHotelSavedData(savedItemData)) return undefined
  if (typeof savedItemData.packageDuration === 'number') return savedItemData.packageDuration
  if (!('durationInDays' in savedItemData)) return undefined
  return typeof savedItemData.durationInDays === 'number' ? savedItemData.durationInDays : undefined
}

function getItemTotalDays(row: TripListRow): number | undefined {
  if (row.kind !== 'item') return undefined
  if (!('totalDays' in row.item)) return undefined
  return row.item.totalDays
}

function getItemDayOfStay(row: TripListRow): number | undefined {
  if (row.kind !== 'item') return undefined
  if (!('dayOfStay' in row.item)) return undefined
  return row.item.dayOfStay
}

export function isTripListRowComplexMove(row: TripListRow): boolean {
  if (row.kind === 'transport') return true
  if (row.rowType === 'flight' || row.rowType === 'transport') return true
  if (row.rowType !== 'accommodation') return false

  return (
    (getItemTotalDays(row) ?? 1) > 1 ||
    (getItemDayOfStay(row) ?? 1) > 1 ||
    (getSavedStayDuration(row) ?? 1) > 1
  )
}

export function canMoveTripListRowToAnotherDay(row: TripListRow): boolean {
  return row.kind === 'item' && !isTripListRowComplexMove(row)
}

export function canAskAiToMoveTripListRow(row: TripListRow): boolean {
  return isTripListRowComplexMove(row)
}

function formatDayOptionTitle(intl: IntlShape, dayNumber: number, date: string | undefined): string {
  if (!date) {
    return intl.formatMessage(
      {
        id: 'nKq1zQQctl_T9caEJO6m',
        defaultMessage: 'Day {dayNumber}',
        description: 'Label for a specific day number in the trip planner itinerary',
      },
      { dayNumber },
    )
  }

  return intl.formatMessage(
    {
      id: 'Fxkqotz9-cwtqkBko91-',
      defaultMessage: 'Day {dayNumber} · {date}',
      description: 'Move-to-day dropdown option label for a specific trip planner day',
    },
    {
      dayNumber,
      date: formatDayLabel(date, intl),
    },
  )
}

function getMoveDayOptionsForStops(
  stops: Array<TripStop>,
  intl: IntlShape,
  isTargetDaySelected: (dayId: number) => boolean,
): Array<TripListMoveDayOption> {
  return stops.flatMap(stop => [...stop.days]
    .sort((a, b) => a.dayNumber - b.dayNumber)
    .map((day): TripListMoveDayOption => ({
      id: `day:${day.id}`,
      title: formatDayOptionTitle(intl, day.dayNumber, day.date),
      subtitle: stop.placeName,
      selected: isTargetDaySelected(day.id),
      target: { targetType: 'day', dayId: day.id },
    })))
}

function getUnplannedMoveDayTitle(intl: IntlShape): string {
  return intl.formatMessage({
    id: 'O6R6NLzQ-6hZlM_sqtvo',
    defaultMessage: 'To be planned',
    description: 'Date column label for trip planner list rows that are not assigned to a specific day yet',
  })
}

export function getTripListMoveDayOptions(row: TripListRow, stops: Array<TripStop>, intl: IntlShape): Array<TripListMoveDayOption> {
  const dayOptions = getMoveDayOptionsForStops(stops, intl, dayId => row.day?.id === dayId)
  const unplannedOption: TripListMoveDayOption = {
    id: 'unplanned',
    title: getUnplannedMoveDayTitle(intl),
    subtitle: row.stop.placeName,
    selected: row.kind === 'item' && !row.day,
    target: { targetType: 'unplanned' },
  }

  return [
    ...dayOptions,
    unplannedOption,
  ]
}

export function getTripListBulkMoveDayOptions(rows: Array<TripListRow>, stops: Array<TripStop>, intl: IntlShape): Array<TripListMoveDayOption> {
  if (rows.length === 0) return []

  const dayOptions = getMoveDayOptionsForStops(stops, intl, dayId => rows.every(row => row.day?.id === dayId))
  const unplannedOption: TripListMoveDayOption = {
    id: 'unplanned',
    title: getUnplannedMoveDayTitle(intl),
    subtitle: intl.formatMessage({
      id: '5kfo1SPZywNAUz1-bEGt',
      defaultMessage: 'Selected items',
      description: 'Subtitle for the To be planned bulk move option in the trip planner list quick actions menu',
    }),
    selected: rows.every(row => row.kind === 'item' && !row.day),
    target: { targetType: 'unplanned' },
  }

  return [
    ...dayOptions,
    unplannedOption,
  ]
}
